import { getBrand, normalizeHostname, resolveBrandId } from "./brandConfig";

/** Origin of the current tab. The environment variable is used only when that origin is absent. */
export function getAppOrigin(pageLocation) {
  const source =
    pageLocation !== undefined
      ? pageLocation
      : typeof window !== "undefined"
        ? window.location
        : undefined;
  const rawOrigin = source && source.origin != null ? String(source.origin) : "";
  if (rawOrigin !== "") {
    return rawOrigin.replace(/\/$/, "");
  }
  return (process.env.REACT_APP_APP_URL || "").replace(/\/$/, "");
}

const MAIN_MARKETING_HOSTS = new Set([
  "fractolabs.com",
  "www.fractolabs.com",
  "staging.fractolabs.com",
  "localhost",
  "127.0.0.1",
]);

const FRACTOLABS_APEX = "fractolabs.com";

/** Legacy subdomain → root landing key for `/`. */
export const SUBDOMAIN_ROOT_LANDING = {
  partner: "partner",
  roofing: "roofing",
  inbnd: "realEstate",
  ai: "loginRedirect",
};

export function getHostname() {
  return typeof window !== "undefined" ? window.location.hostname.toLowerCase() : "";
}

export function currentSearch() {
  return typeof window !== "undefined" ? window.location.search : "";
}

export function isMainMarketingHost(hostname = getHostname()) {
  return MAIN_MARKETING_HOSTS.has(normalizeHostname(hostname));
}

export function isSettrHqHost(
  hostname = getHostname(),
  search = currentSearch(),
  devBrand = process.env.REACT_APP_DEV_BRAND,
) {
  return resolveBrandId(hostname, search, undefined, devBrand) === "settrhq";
}

/** Single place that picks the brand for the current host. Unknown hosts return null. */
export function getSiteBrand(
  hostname = getHostname(),
  search = currentSearch(),
  devBrand = process.env.REACT_APP_DEV_BRAND,
) {
  return getBrand(hostname, search, undefined, devBrand);
}

/** Logo for the current host, or null when the host has no brand. */
export function getSiteLogo(
  hostname = getHostname(),
  search = currentSearch(),
  devBrand = process.env.REACT_APP_DEV_BRAND,
) {
  const brand = getSiteBrand(hostname, search, devBrand);
  if (!brand) return null;
  return { src: brand.logo.default, alt: brand.name };
}

/** Dark-gray header mark for the light roofing landings, or null when the host has no brand. */
export function getLightLandingLogo(
  hostname = getHostname(),
  search = currentSearch(),
  devBrand = process.env.REACT_APP_DEV_BRAND,
) {
  const brand = getSiteBrand(hostname, search, devBrand);
  if (!brand) return null;
  return { src: brand.logo.onLight, alt: brand.name };
}

const RESEARCH_LAB_PHRASE = "FRACTOLABS RESEARCH LAB";
const RESEARCH_LAB_TOKEN = "\u0000FRACTOLABS_RESEARCH_LAB\u0000";

function replaceLandingBrandText(text) {
  return text
    .replaceAll(RESEARCH_LAB_PHRASE, RESEARCH_LAB_TOKEN)
    .replaceAll("FractoLabs", "SettrHQ")
    .replaceAll(RESEARCH_LAB_TOKEN, RESEARCH_LAB_PHRASE);
}

/** On SettrHQ, swap the FractoLabs name in landing copy. The research-lab line stays. */
export function applyLandingBrand(
  value,
  hostname = getHostname(),
  search = currentSearch(),
  devBrand = process.env.REACT_APP_DEV_BRAND,
) {
  if (!isSettrHqHost(hostname, search, devBrand)) return value;
  if (typeof value === "string") return replaceLandingBrandText(value);
  if (Array.isArray(value)) {
    return value.map((item) => applyLandingBrand(item, hostname, search, devBrand));
  }
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [
        key,
        applyLandingBrand(item, hostname, search, devBrand),
      ]),
    );
  }
  return value;
}

/** Returns subdomain label for *.fractolabs.com, or null for apex/www/non-fractolabs hosts. */
export function getFractoLabsSubdomain(hostname = getHostname()) {
  const normalized = hostname.toLowerCase();
  if (normalized === FRACTOLABS_APEX || normalized === `www.${FRACTOLABS_APEX}`) {
    return null;
  }
  if (!normalized.endsWith(`.${FRACTOLABS_APEX}`)) {
    return null;
  }
  const subdomain = normalized.slice(0, -(FRACTOLABS_APEX.length + 1));
  if (!subdomain || subdomain.includes(".")) {
    return null;
  }
  return subdomain;
}

export function hasLandingUnlockParam(search = "") {
  const normalized = search.startsWith("?") ? search.slice(1) : search;
  return new URLSearchParams(normalized).has("l");
}

/**
 * Resolves which root landing to show at `/`.
 * @returns {"main"|"settrhq"|"partner"|"roofing"|"realEstate"|"loginRedirect"|"stub"}
 */
export function resolveRootLandingKey(
  hostname = getHostname(),
  search = currentSearch(),
  storedLeadId = null,
  devBrand = process.env.REACT_APP_DEV_BRAND,
) {
  const host = normalizeHostname(hostname);
  const brandId = resolveBrandId(host, search, undefined, devBrand);

  if (brandId === null) return "stub";
  if (brandId === "settrhq") return "settrhq";
  if (brandId === "fractolabs" && isMainMarketingHost(host)) return "main";

  const subdomain = getFractoLabsSubdomain(host);
  const key = subdomain === null ? "stub" : (SUBDOMAIN_ROOT_LANDING[subdomain] ?? "stub");
  if (
    key === "stub"
    && brandId === "fractolabs"
    && (hasLandingUnlockParam(search) || Boolean(storedLeadId))
  ) {
    return "main";
  }
  return key;
}
